// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { redirectTo, setStatusCode } from "@insite/client-framework/ServerSideRendering";
import {
    DoNotFollowLinks,
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useEffect } from "react";
import { connect } from "react-redux";

const LIST_PAGE_TYPES = ["VmiBlogListPage", "VmiEventsListPage"];

const getListingPageUrl = (state: ApplicationState, parentId?: string) => {
    if (!parentId) {
        return;
    }

    let currentLink = getPageLinkByNodeId(state, parentId);
    while (currentLink) {
        if (currentLink.type && LIST_PAGE_TYPES.includes(currentLink.type)) {
            return currentLink.url;
        }

        currentLink = currentLink.parentId ? getPageLinkByNodeId(state, currentLink.parentId) : undefined;
    }

    return undefined;
};

const mapStateToProps = (state: ApplicationState) => ({
    listingPageUrl: getListingPageUrl(state, getCurrentPage(state).parentId),
});

type Props = PageProps & ReturnType<typeof mapStateToProps> & HasHistory;

const VmiFolderPage = ({ listingPageUrl, history }: Props) => {
    const { isInShell } = useShellContext();

    useEffect(() => {
        if (!isInShell && listingPageUrl) {
            history.replace(listingPageUrl);
        }
    }, [isInShell, listingPageUrl, history]);

    if (IS_SERVER_SIDE && listingPageUrl && !isInShell) {
        setStatusCode(302);
        redirectTo(listingPageUrl);
    }

    return null;
};

const pageModule: PageModule = {
    component: connect(mapStateToProps)(withHistory(VmiFolderPage)),
    definition: {
        displayName: "Folder",
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
        allowedParents: ["VmiBlogListPage", "VmiEventsListPage", "VmiEventCoursePage", "VmiFolderPage"],
        allowedChildren: ["VmiBlogDetailsPage", "VmiEventDetailsPage", "VmiEventCoursePage", "VmiFolderPage"],
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            DoNotFollowLinks,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "VmiFolderPage" instead of this constant.
 */
export const VmiFolderPageContext = "VmiFolderPage";
